import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import ProductReviews from '../components/ProductReviews';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaStar, FaBox, FaMapMarkerAlt, FaPhone, FaEnvelope, FaEdit, FaTrash, FaShoppingCart, FaHeart, FaComments, FaRegHeart, FaUser } from 'react-icons/fa';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [interested, setInterested] = useState(false);
  const [interestedBuyers, setInterestedBuyers] = useState([]);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [purchasedOrderId, setPurchasedOrderId] = useState(null);

  useEffect(() => {
    fetchProduct();
    if (user?.userType === 'buyer') {
      checkWishlist();
      checkPurchaseHistory();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data.product);
      
      // Check if user is interested
      if (user && response.data.product.interests) {
        const isInterested = response.data.product.interests.some(
          interest => interest.buyer.toString() === user._id
        );
        setInterested(isInterested);
      }

      // Fetch interested buyers if seller and owner
      const isSeller = user?.userType === 'seller';
      const isOwner = isSeller && response.data.product.seller._id === user._id;
      if (isOwner) {
        fetchInterestedBuyers();
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterestedBuyers = async () => {
    try {
      const response = await api.get(`/products/${id}/interests`);
      setInterestedBuyers(response.data.interests || []);
    } catch (error) {
      console.error('Failed to fetch interested buyers:', error);
    }
  };

  const handleInterest = async () => {
    try {
      await api.post(`/products/${id}/interest`);
      setInterested(true);
      toast.success('Interest registered! Seller will contact you.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register interest');
    }
  };

  const checkWishlist = async () => {
    try {
      const response = await api.get(`/wishlist/check/${id}`);
      setIsInWishlist(response.data.isInWishlist);
    } catch (error) {
      console.error('Failed to check wishlist:', error);
    }
  };

  const checkPurchaseHistory = async () => {
    try {
      // Check if user has a delivered order with this product
      const response = await api.get('/orders');
      const orders = response.data.orders || [];
      
      const deliveredOrder = orders.find(order => 
        order.status === 'delivered' && 
        order.items?.some(item => 
          (item.product?._id || item.product) === id
        )
      );
      
      if (deliveredOrder) {
        setHasPurchased(true);
        setPurchasedOrderId(deliveredOrder._id);
      }
    } catch (error) {
      console.error('Failed to check purchase history:', error);
    }
  };

  const toggleWishlist = async () => {
    try {
      if (isInWishlist) {
        await api.delete(`/wishlist/${id}`);
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${id}`);
        setIsInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update wishlist');
    }
  };

  const handleStartChat = async () => {
    try {
      const response = await api.post('/chat/start', {
        recipientId: product.seller._id,
        productId: product._id,
        message: `Hi, I'm interested in "${product.title}"`
      });
      toast.success('Chat started!');
      navigate(`/chat/${response.data.chat._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start chat');
    }
  };

  const handleStartChatWithBuyer = async (buyerId) => {
    try {
      const response = await api.post('/chat/start', {
        recipientId: buyerId,
        productId: product._id,
        message: `Hi, I saw you're interested in "${product.title}". How can I help you?`
      });
      toast.success('Chat started!');
      navigate(`/chat/${response.data.chat._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start chat');
    }
  };

  const handleCreateOrder = async () => {
    try {
      // Create order directly
      const orderData = {
        items: [{
          productId: product._id,
          quantity: quantity
        }],
        paymentMethod: 'upi' // Default payment method
      };

      const response = await api.post('/orders', orderData);
      toast.success('Order placed successfully! Redirecting...');
      
      // Navigate to order detail page
      setTimeout(() => {
        navigate(`/orders/${response.data.order._id}`);
      }, 1500);
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.message || 'Failed to place order');
    }
  };

  const handleEdit = () => {
    navigate(`/products/${id}/edit`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.delete(`/products/${id}`);
        toast.success('Product deleted successfully');
        navigate('/products');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete product');
      }
    }
  };

  const calculateFinalPrice = () => {
    if (!product) return 0;
    const { basePrice, gst, discountPercent } = product.pricing;
    const discountedPrice = basePrice - (basePrice * (discountPercent / 100));
    const finalPrice = discountedPrice + (discountedPrice * (gst / 100));
    return finalPrice;
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading product details...</div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="error-message">
          <h2>Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Back to Products
          </button>
        </div>
      </Layout>
    );
  }

  const isSeller = user?.userType === 'seller';
  const isOwner = isSeller && product.seller._id === user._id;
  const finalPrice = calculateFinalPrice();

  return (
    <Layout>
      <div className="product-detail">
        <div className="product-detail-header">
          <button className="btn-back" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          {isOwner && (
            <div className="owner-actions">
              <button className="btn-edit" onClick={handleEdit}>
                <FaEdit /> Edit
              </button>
              <button className="btn-delete" onClick={handleDelete}>
                <FaTrash /> Delete
              </button>
            </div>
          )}
        </div>

        <div className="product-detail-content">
          {/* Left Column - Images */}
          <div className="product-images">
            <div className="main-image">
              {product.images && product.images.length > 0 ? (
                <img src={product.images[0].url} alt={product.images[0].alt || product.title} />
              ) : (
                <div className="no-image">
                  <FaBox size={80} />
                  <p>No image available</p>
                </div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="thumbnail-images">
                {product.images.slice(1, 5).map((img, index) => (
                  <img key={index} src={img.url} alt={img.alt || `${product.title} ${index + 2}`} />
                ))}
              </div>
            )}
          </div>

          {/* Middle Column - Product Info */}
          <div className="product-info">
            <div className="product-badges">
              <span className={`badge badge-${product.status}`}>{product.status}</span>
              {product.verified && <span className="badge badge-verified">✓ Verified</span>}
              <span className="badge badge-category">{product.category}</span>
              <span className="badge badge-type">{product.type}</span>
            </div>

            <h1 className="product-title">{product.title}</h1>

            <div className="product-rating">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} color={i < Math.floor(product.ratings.average) ? '#fbbf24' : '#d1d5db'} />
                ))}
              </div>
              <span className="rating-text">
                {product.ratings.average.toFixed(1)} ({product.ratings.count} reviews)
              </span>
              <span className="views-text">• {product.views} views</span>
            </div>

            <div className="product-pricing">
              <div className="price-main">
                <span className="currency">{product.pricing.currency}</span>
                <span className="price">{finalPrice.toFixed(2)}</span>
                <span className="unit">/ {product.pricing.unit}</span>
              </div>
              {product.pricing.discountPercent > 0 && (
                <div className="price-discount">
                  <span className="original-price">₹{product.pricing.basePrice}</span>
                  <span className="discount-badge">{product.pricing.discountPercent}% OFF</span>
                </div>
              )}
              <div className="price-details">
                <span>Base Price: ₹{product.pricing.basePrice}</span>
                <span>GST: {product.pricing.gst}%</span>
                <span>MOQ: {product.pricing.moq} {product.pricing.unit}(s)</span>
              </div>
            </div>

            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="product-specifications">
                <h3>Specifications</h3>
                <table className="specs-table">
                  <tbody>
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <tr key={key}>
                        <td className="spec-key">{key}</td>
                        <td className="spec-value">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="product-inventory">
              <h3>Availability</h3>
              <div className="inventory-info">
                <span className={`availability ${product.inventory.available ? 'in-stock' : 'out-of-stock'}`}>
                  {product.inventory.available ? '✓ In Stock' : '✗ Out of Stock'}
                </span>
                {product.inventory.quantity > 0 && (
                  <span className="quantity">Quantity: {product.inventory.quantity}</span>
                )}
                {product.inventory.warehouse && (
                  <span className="warehouse">
                    <FaMapMarkerAlt /> {product.inventory.warehouse}
                  </span>
                )}
              </div>
            </div>

            {product.tags && product.tags.length > 0 && (
              <div className="product-tags">
                {product.tags.map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Seller & Actions */}
          <div className="product-sidebar">
            <div className="seller-card">
              <h3>Seller Information</h3>
              <div className="seller-info">
                {product.seller.profileImage && (
                  <img src={product.seller.profileImage} alt={product.seller.businessName} className="seller-avatar" />
                )}
                <div className="seller-details">
                  <h4>{product.seller.businessName}</h4>
                  {product.seller.verificationBadge && (
                    <span className="verified-badge">✓ Verified Seller</span>
                  )}
                  <a 
                    href={`/public-profile/${product.seller._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-profile-link"
                  >
                    <FaUser /> View Public Profile
                  </a>
                </div>
              </div>
              <div className="seller-contact">
                {product.seller.phone && (
                  <div className="contact-item">
                    <FaPhone /> {product.seller.phone}
                  </div>
                )}
                {product.seller.email && (
                  <div className="contact-item">
                    <FaEnvelope /> {product.seller.email}
                  </div>
                )}
              </div>

              {/* Chat with Seller Button */}
              {!isOwner && user && (
                <div className="seller-chat-section">
                  <button 
                    className="btn-chat-seller"
                    onClick={handleStartChat}
                  >
                    <FaComments /> Chat with Seller
                  </button>
                </div>
              )}

              {/* Seller Shipping Addresses */}
              {product.seller.addressBook && product.seller.addressBook.length > 0 && (
                <div className="seller-addresses">
                  <h4 className="addresses-title">
                    📦 Shipping Locations
                  </h4>
                  {product.seller.addressBook
                    .filter(addr => {
                      // Only show shipping-related addresses
                      const isShippingType = addr.addressType === 'shop' || addr.addressType === 'warehouse' || addr.addressType === 'office';
                      // Exclude the address if it's already shown in "Ships From" section
                      const isShippingAddress = product.shippingAddress && addr._id === product.shippingAddress.toString();
                      return isShippingType && !isShippingAddress;
                    })
                    .map((address, index) => (
                      <div key={address._id || index} className="shipping-address-card">
                        <div className="address-header">
                          <span className="address-icon">
                            {address.addressType === 'shop' ? '🏪' : 
                             address.addressType === 'warehouse' ? '🏭' : '🏢'}
                          </span>
                          <div className="address-info">
                            <strong>{address.label}</strong>
                            <span className="address-type-badge">{address.addressType}</span>
                          </div>
                        </div>
                        <div className="address-details">
                          <p>{address.addressLine1}</p>
                          {address.addressLine2 && <p>{address.addressLine2}</p>}
                          {address.landmark && <p className="landmark">Near {address.landmark}</p>}
                          <p>{address.city}, {address.state} - {address.pincode}</p>
                          {address.phone && <p className="address-phone">📞 {address.phone}</p>}
                          {address.googleMapLocation && address.googleMapLocation.latitude && (
                            <a 
                              href={`https://www.google.com/maps?q=${address.googleMapLocation.latitude},${address.googleMapLocation.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="map-link"
                            >
                              📍 View on Map
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Product Shipping Address Snapshot (if set) */}
              {product.shippingAddressSnapshot && (
                <div className="shipping-from">
                  <h4 className="addresses-title">
                    📍 Ships From
                  </h4>
                  <div className="shipping-address-card primary">
                    <div className="address-header">
                      <span className="address-icon">
                        {product.shippingAddressSnapshot.addressType === 'shop' ? '🏪' : 
                         product.shippingAddressSnapshot.addressType === 'warehouse' ? '🏭' : '🏢'}
                      </span>
                      <div className="address-info">
                        <strong>{product.shippingAddressSnapshot.label}</strong>
                      </div>
                    </div>
                    <div className="address-details">
                      <p>{product.shippingAddressSnapshot.addressLine1}</p>
                      {product.shippingAddressSnapshot.addressLine2 && 
                        <p>{product.shippingAddressSnapshot.addressLine2}</p>}
                      <p>{product.shippingAddressSnapshot.city}, {product.shippingAddressSnapshot.state} - {product.shippingAddressSnapshot.pincode}</p>
                      {product.shippingAddressSnapshot.phone && 
                        <p className="address-phone">📞 {product.shippingAddressSnapshot.phone}</p>}
                      {product.shippingAddressSnapshot.googleMapLocation && 
                       product.shippingAddressSnapshot.googleMapLocation.latitude && (
                        <a 
                          href={`https://www.google.com/maps?q=${product.shippingAddressSnapshot.googleMapLocation.latitude},${product.shippingAddressSnapshot.googleMapLocation.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="map-link"
                        >
                          📍 View on Map
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isOwner && interestedBuyers.length > 0 && (
              <div className="action-card">
                <h3>👥 Interested Buyers ({interestedBuyers.length})</h3>
                <div className="interested-buyers-list">
                  {interestedBuyers.map((interest, index) => (
                    <div key={index} className="buyer-card">
                      <div className="buyer-info">
                        <h4>{interest.buyer.businessName}</h4>
                        <p className="buyer-type">{interest.buyer.userType}</p>
                      </div>
                      <div className="buyer-contact">
                        <p className="contact-item">
                          <FaPhone /> {interest.buyer.phone}
                        </p>
                        <p className="contact-item">
                          <FaEnvelope /> {interest.buyer.email}
                        </p>
                        {interest.buyer.address && (
                          <p className="contact-item">
                            <FaMapMarkerAlt /> {interest.buyer.address.city}, {interest.buyer.address.state}
                          </p>
                        )}
                      </div>
                      <div className="interest-time">
                        <small>Interested on: {new Date(interest.timestamp).toLocaleDateString()}</small>
                      </div>
                      <div className="buyer-actions">
                        <a href={`tel:${interest.buyer.phone}`} className="btn-contact">
                          <FaPhone /> Call Now
                        </a>
                        <a href={`mailto:${interest.buyer.email}`} className="btn-contact">
                          <FaEnvelope /> Email
                        </a>
                        <button 
                          className="btn-contact"
                          onClick={() => handleStartChatWithBuyer(interest.buyer._id)}
                        >
                          <FaComments /> Chat
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isOwner && user?.userType === 'buyer' && (
              <div className="action-card">
                <h3>Interested in this product?</h3>
                
                <div className="quantity-selector">
                  <label>Quantity ({product.pricing.unit})</label>
                  <div className="quantity-input">
                    <button onClick={() => setQuantity(Math.max(product.pricing.moq, quantity - 1))}>-</button>
                    <input 
                      type="number" 
                      value={quantity} 
                      onChange={(e) => setQuantity(Math.max(product.pricing.moq, parseInt(e.target.value) || product.pricing.moq))}
                      min={product.pricing.moq}
                    />
                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  </div>
                  <span className="moq-info">Min order: {product.pricing.moq}</span>
                </div>

                <div className="total-price">
                  <span>Total Amount:</span>
                  <span className="total">₹{(finalPrice * quantity).toFixed(2)}</span>
                </div>

                <div className="action-buttons">
                  <button 
                    className="btn-primary btn-full"
                    onClick={handleCreateOrder}
                    disabled={!product.inventory.available}
                  >
                    <FaShoppingCart /> Place Order
                  </button>
                  
                  <div className="action-row">
                    <button 
                      className={`btn-wishlist ${isInWishlist ? 'active' : ''}`}
                      onClick={toggleWishlist}
                    >
                      {isInWishlist ? <FaHeart /> : <FaRegHeart />}
                      {isInWishlist ? 'Saved' : 'Save'}
                    </button>
                    
                    <button 
                      className={`btn-interest ${interested ? 'interested' : ''}`}
                      onClick={handleInterest}
                      disabled={interested}
                    >
                      <FaHeart /> {interested ? 'Interest Registered' : 'Show Interest'}
                    </button>
                  </div>
                </div>

                <div className="info-text">
                  <small>* Seller will contact you to confirm order details</small>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Reviews Section */}
        <div className="product-reviews-section">
          <ProductReviews 
            productId={id}
            showSubmitForm={user?.userType === 'buyer'}
            orderId={purchasedOrderId}
          />
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetail;
